import { captureException } from '@sentry/react'
import { ErrorKeys } from 'consts/errorKeys'
import { getAiLibImages_API } from 'api'
import slice from 'store/features/aiLibImage/slice'
import { aiLibImageSelectors } from 'store/features/aiLibImage/selectors'
import { aiImageSelectors } from 'store/features/aiImage/selectors'
import { projectSelectors } from 'store/features/project/selectors'
import { TAppDispatch, TGetState } from 'store/types'
import { IAiLibImageListParams } from 'store/types/aiLibImage'
import { handleApiError } from 'store/utils'
import { ApiError, ReduxThunkError } from 'utils/errors'

const { actions } = slice

const getHistory =
  (params: IAiLibImageListParams) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const isScoped = aiLibImageSelectors.isHistoryScopedToCurrentProject(state)
    const currentProjectId = projectSelectors.id(state)

    if (isScoped && !currentProjectId) {
      dispatch(actions.setHistoryList([]))
      dispatch(actions.setHistoryTotalCount(0))
      dispatch(actions.setIsLoaded(true))
      dispatch(actions.setIsInitialLoading(false))
      return
    }

    const isLoadMore = params.offset !== undefined && params.offset > 0

    const isAlreadyLoading = isLoadMore
      ? aiLibImageSelectors.isLoadingMore(state)
      : aiLibImageSelectors.isInitialLoading(state)

    if (isAlreadyLoading) return

    if (isLoadMore) {
      dispatch(actions.setIsLoadingMore(true))
    } else {
      dispatch(actions.setIsInitialLoading(true))
    }

    const activeFilters = aiLibImageSelectors.activeFilters(state)

    try {
      const response = await getAiLibImages_API({
        ...params,
        videoProjectId: isScoped ? Number(currentProjectId) : undefined,
        ...activeFilters
      })
      const { imageProjects, totalCount } = response.data

      if (isLoadMore) {
        dispatch(actions.appendHistoryList(imageProjects))
      } else {
        dispatch(actions.setHistoryList(imageProjects))
        dispatch(actions.setIsLoaded(true))
      }
      dispatch(actions.setHistoryTotalCount(totalCount))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
    } finally {
      if (isLoadMore) {
        dispatch(actions.setIsLoadingMore(false))
      } else {
        dispatch(actions.setIsInitialLoading(false))
      }
    }
  }

const changeModel =
  (modelCode: string | null) =>
  (dispatch: TAppDispatch, getState: TGetState) => {
    const state = getState()
    const aspectRatio = aiLibImageSelectors.selectedAspectRatio(state)

    const model = modelCode
      ? (aiImageSelectors.modelsData(state).find(m => m.code === modelCode) ??
        null)
      : null

    dispatch(
      actions.setFilter({
        modelCode,
        aspectRatio:
          !model ||
          aspectRatio === null ||
          model.possibleAspectRatios.includes(aspectRatio)
            ? aspectRatio
            : null
      })
    )
  }

export const aiLibImageThunks = {
  ...actions,
  getHistory,
  changeModel
}
